import React, { useMemo } from "react";
import { FaCircleCheck } from "react-icons/fa6";
import Tooltip from "../../components/Tooltip.jsx";
import { PrimaryButton } from "../../components/Buttons.jsx";
import {
  purchaseAddOn,
  upgradeSubscription,
} from "../../api/settings.js";
import { addToast, getMerchants } from "../../store/app/action.js";
import ModalBasic from "../../components/ModalBasic.jsx";
import { useDispatch, useSelector } from "react-redux";
import { formatValue } from "../../utils/Utils.js";
import Loader from "../../components/Loader.jsx";
import CRMPricingSlider from "../../components/CRMPricingSlider.jsx";
import { usePricing } from "../../hooks/usePricing.js";
import PlanCard from "../../components/PlanCard.jsx";

const PlanModal = ({ isOpen, setIsOpen }) => {
  const { selectedMerchant } = useSelector((state) => state.app);
  const [upgrading, setUpgrading] = React.useState(false);
  const [upgraded, setUpgraded] = React.useState(false);
  const [crmModalOpen, setCrmModalOpen] = React.useState(false);
  const dispatch = useDispatch();
  const { highestBasicPrice, highestGrowthPrice, addonPrice, tiers, pricing } = usePricing();

  const locations = useMemo(() => {
    if (!selectedMerchant) {
      return 0;
    }
    return selectedMerchant?.locations?.filter((location) => location.enabled)
      .length;
  }, [selectedMerchant]);

  const myGrowthPrice = useMemo(() => {
    if (!pricing || !selectedMerchant) {
      return "-";
    }
    let myTier = pricing?.growth?.tiers.find((tier) => {
      return tier.up_to >= locations;
    });
    if (!myTier) {
      myTier = pricing?.growth?.tiers[pricing?.growth?.tiers.length - 1];
    }
    const unitPrice = myTier.unit_amount / 100;
    const totalPrice = unitPrice * locations;
    return (
      <div>
        <div>Your Price:</div>
        {formatValue(unitPrice).replace(".00", "")} x {locations}{" "}
        location{locations !== 1 ? "s" : ""} ={" "}
        <span className={"font-bold"}>
          {" "}
          {formatValue(totalPrice).replace(".00", "")}{" "}
          / month
        </span>
      </div>
    );
  }, [pricing, locations]);

  const seoBoostPrice = useMemo(() => {
    return addonPrice("seo-boost");
  }, [addonPrice]);
  
  const crmPrice = useMemo(() => {
    return addonPrice("crm");
  }, [addonPrice]);

  return (
    <ModalBasic
      modalOpen={isOpen}
      setModalOpen={setIsOpen}
      title={"Change Plan"}
      width={"3xl"}
    >
      <div className="text-sm">
        <div className="overflow-x-auto w-full flex justify-center items-center pt-4">
          <div className="md:min-w-max flex flex-col md:flex-row gap-8 md:gap-4">
            <PlanCard
              planType="basic"
              price={highestBasicPrice}
              tiers={tiers?.basic}
              tierPosition="right"
              actionButton={
                selectedMerchant?.direct_subscription === "basic" ? (
                  <PrimaryButton disabled>Current Plan</PrimaryButton>
                ) : (
                  <PrimaryButton disabled>
                    Contact us to downgrade
                  </PrimaryButton>
                )
              }
            />

            <PlanCard
              planType="growth"
              price={highestGrowthPrice}
              tiers={tiers?.growth}
              tierPosition="left"
              actionButton={
                selectedMerchant?.direct_subscription === "basic" ? (
                  <div className="flex flex-col gap-2">
                    {myGrowthPrice}
                    <PrimaryButton
                      loading={upgrading}
                      success={upgraded}
                      onClick={async () => {
                        setUpgrading(true);
                        try {
                          await upgradeSubscription("growth");
                          setUpgraded(true);
                          dispatch(
                            addToast({
                              type: "success",
                              message: "Upgraded to Growth Plan",
                              timer: 5,
                            })
                          );
                          dispatch(getMerchants());
                          setTimeout(() => {
                            setUpgrading(false);
                            setUpgraded(false);
                            setIsOpen(false);
                          }, 5000);
                        } catch (e) {
                          let message = "An error occurred";
                          try {
                            const serverMessage = e.response?.data?.error;
                            if (serverMessage) {
                              message = serverMessage;
                            }
                          } catch {}
                          dispatch(
                            addToast({
                              type: "error",
                              message: message,
                              timer: 5,
                            })
                          );
                        } finally {
                          setUpgrading(false);
                        }
                      }}
                    >
                      Upgrade Now
                    </PrimaryButton>
                  </div>
                ) : (
                  <PrimaryButton disabled>Current Plan</PrimaryButton>
                )
              }
            />
          </div>
        </div>
        <div className={"items-center"}>
          <h2 className={"text-lg font-bold text-center w-full my-4"}>
            Add-ons
          </h2>
          <div className={"flex flex-col gap-4 items-center"}>
            {(() => {
              // Create addon objects with their subscription status
              const addons = [
                {
                  id: 'seo-boost',
                  name: 'SEO Boost Add-on',
                  description: 'Enhance your visibility for customers to discover you online, driving more traffic and sales to your business.',
                  features: [
                    'Optimize your website for organic traffic',
                    'Customize your website with advanced features'
                  ],
                  price: seoBoostPrice,
                  isSubscribed: selectedMerchant?.direct_addons?.includes("seo-boost") || selectedMerchant?.direct_subscription !== "basic",
                  isIncluded: selectedMerchant?.direct_subscription !== "basic"
                },
                {
                  id: 'crm',
                  name: 'Customer Engagement',
                  description: 'Enhance customer experience by managing relationships, tracking interactions, and improving marketing efforts with our efficient customer relationship management tools.',
                  features: [
                    'Automated campaigns such as cart abandonment, weekly deals, re-engagement to improve customer retention',
                    'Custom email blasts',
                    'Upload customer list'
                  ],
                  price: crmPrice,
                  isSubscribed: selectedMerchant?.direct_addons?.includes("crm"),
                  isIncluded: false
                }
              ];

              // Sort: unsubscribed first, then subscribed
              const sortedAddons = addons.sort((a, b) => {
                if (a.isSubscribed === b.isSubscribed) return 0;
                return a.isSubscribed ? 1 : -1;
              });

              return sortedAddons.map((addon) => (
                <div key={addon.id} className="border border-slate-200 rounded-lg py-2 px-4 w-full md:w-[700px] flex flex-col md:flex-row items-center ">
                  <div>
                    <h3 className="font-bold text-lg my-2">{addon.name}</h3>
                    <p className="text-sm md">{addon.description}</p>
                    {addon.features.map((feature, index) => (
                      <div key={index} className="flex gap-2 items-center my-4">
                        <FaCircleCheck className="text-green-600 text-lg flex-shrink-0" />
                        <span className="leading-tight">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div>
                    <div className="w-full md:w-[125px] ml-4 md:text-right">
                      {addon.id === 'seo-boost' ? (
                        selectedMerchant?.direct_subscription === "basic" ? (
                          addon.isSubscribed ? (
                            <PrimaryButton disabled>Subscribed</PrimaryButton>
                          ) : (
                            <PrimaryButton
                              loading={upgrading}
                              success={upgraded}
                              onClick={async () => {
                                setUpgrading(true);
                                try {
                                  await purchaseAddOn("seo-boost");
                                  setUpgraded(true);
                                  dispatch(
                                    addToast({
                                      type: "success",
                                      message: "SEO Boost add-on enabled.",
                                      timer: 5,
                                    })
                                  );
                                  dispatch(getMerchants());
                                  setTimeout(() => {
                                    setUpgrading(false);
                                    setUpgraded(false);
                                    setIsOpen(false);
                                  }, 5000);
                                } catch (e) {
                                  let message = "An error occurred";
                                  try {
                                    const serverMessage = e.response?.data?.error;
                                    if (serverMessage) {
                                      message = serverMessage;
                                    }
                                  } catch {}
                                  dispatch(
                                    addToast({
                                      type: "error",
                                      message: message,
                                      timer: 5,
                                    })
                                  );
                                } finally {
                                  setUpgrading(false);
                                }
                              }}
                            >
                              Enable SEO Boost Now
                            </PrimaryButton>
                          )
                        ) : (
                          <PrimaryButton disabled>Included in plan</PrimaryButton>
                        )
                      ) : addon.id === 'crm' ? (
                        addon.isSubscribed ? (
                          <>
                            <PrimaryButton disabled>Subscribed</PrimaryButton>
                            <div className="text-right mt-2">
                              <button
                                className="text-indigo-600 hover:text-indigo-800 text-sm underline"
                                onClick={() => {
                                  setCrmModalOpen(true);
                                }}
                              >
                                learn more
                              </button>
                            </div>
                          </>
                        ) : (
                          <PrimaryButton onClick={() => setCrmModalOpen(true)}>
                            Learn More
                          </PrimaryButton>
                        )
                      ) : null}
                    </div>
                    {addon.id === 'seo-boost' ? (
                      <div className={"text-right mt-2"}>
                        <div className="text-2xl font-bold flex items-center justify-end ">
                          {addon.price === "-" ? (
                            <Loader size={8} hideText />
                          ) : (
                            formatValue(addon.price / 100).replace(".00", "")
                          )}
                          <span className="text-sm font-normal">/mo </span>{" "}
                          <span className="inline-block relative left-1">
                            <Tooltip size="lg" position={"left"}>
                              <span className="text-sm font-normal p-0">
                                Pay one price for all your locations
                              </span>
                            </Tooltip>
                          </span>
                        </div>
                      </div>
                    ) : addon.id === 'crm' ? (
                      addon.price && Array.isArray(addon.price) && addon.price.length > 0 && (
                        <div className={"text-right mt-2"}>
                          Starts at
                          <div className="text-2xl font-bold flex items-center justify-end ">
                            {addon.price === "-" ? (
                              <Loader size={8} hideText />
                            ) : (
                              formatValue(addon.price[0].flat_amount / 100).replace(
                                ".00",
                                ""
                              )
                            )}
                            <span className="text-sm font-normal">/mo </span>
                          </div>
                        </div>
                      )
                    ) : null}
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>
      </div>

      {/* CRM Pricing Modal */}
      <ModalBasic
        modalOpen={crmModalOpen}
        setModalOpen={setCrmModalOpen}
        title="Customer Engagement Pricing"
        width="2xl"
      >
        <div className="text-sm">
          <div className="mb-6">
            <h3 className="font-bold text-lg mb-2">
              Customer Relationship Management
            </h3>
            <p className="text-slate-600 mb-4">
              You will be charged monthly based on the number of customers you
              have. Adjust the slider below to see pricing for your customer
              base size.{" "}
            </p>
          </div>

          {/* Interactive Slider */}
          <div className="mb-8">
            <CRMPricingSlider
              tiers={crmPrice && Array.isArray(crmPrice) ? crmPrice : []}
              onPriceChange={(tier) => {
                // Optional: Handle price change if needed
              }}
            />
          </div>

          {/* All Tiers Overview */}
          <div className="mb-6">
            <h4 className="font-semibold mb-3">Complete Pricing Tiers:</h4>
            <div className="space-y-2">
              {crmPrice &&
                Array.isArray(crmPrice) &&
                crmPrice.slice(0, -1).map((tier, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center py-2 border-b border-slate-100"
                  >
                    <div className="text-sm">
                      {index === 0
                        ? `Up to ${tier.up_to.toLocaleString()}`
                        : `${(
                            crmPrice[index - 1].up_to + 1
                          ).toLocaleString()} - ${tier.up_to.toLocaleString()}`}{" "}
                      customers
                    </div>
                    <div className="font-semibold">
                      ${(tier.flat_amount / 100).toFixed(2)}/month
                    </div>
                  </div>
                ))}
              {crmPrice && Array.isArray(crmPrice) && crmPrice.length > 0 && (
                <div className="flex justify-between items-center py-2">
                  <div className="text-sm">
                    {crmPrice[crmPrice.length - 1].up_to
                      ? `${(
                          crmPrice[crmPrice.length - 1].up_to + 1
                        ).toLocaleString()}+ customers`
                      : `${(
                          crmPrice[crmPrice.length - 2].up_to + 1
                        ).toLocaleString()}+ customers`}
                  </div>
                  <div className="font-semibold text-indigo-600">
                    Contact Us
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="p-4 bg-indigo-50 rounded-lg">
            <h4 className="font-semibold mb-2">What's included:</h4>
            <ul className="space-y-1 text-sm">
              <li className="flex items-center">
                <FaCircleCheck className="text-green-600 mr-2 flex-shrink-0" />
                1 million emails per month
              </li>

              <li className="flex items-center">
                <FaCircleCheck className="text-green-600 mr-2 flex-shrink-0" />
                Custom email blasts and marketing campaigns
              </li>
              <li className="flex items-center">
                <FaCircleCheck className="text-green-600 mr-2 flex-shrink-0" />
                Customer list upload and management
              </li>
              <li className="flex items-center">
                <FaCircleCheck className="text-green-600 mr-2 flex-shrink-0" />
                Coming soon: Automated campaigns (cart abandonment,
                re-engagement, etc)
              </li>
              <li className="flex items-center">
                <FaCircleCheck className="text-green-600 mr-2 flex-shrink-0" />
                Coming soon: Customer interaction tracking and analytics
              </li>
            </ul>
          </div>
          <div className="flex justify-end mt-4">
            {selectedMerchant.direct_addons?.includes("crm") ? (
              <>
                <PrimaryButton disabled>Subscribed</PrimaryButton>
              </>
            ) : (
              <PrimaryButton
                loading={upgrading}
                success={upgraded}
                onClick={async () => {
                  setUpgrading(true);
                  try {
                    await purchaseAddOn("crm");
                    setUpgraded(true);
                    dispatch(
                      addToast({
                        type: "success",
                        message: "Customer Engagement enabled.",
                        timer: 5,
                      })
                    );
                    dispatch(getMerchants());
                    setTimeout(() => {
                      setUpgrading(false);
                      setUpgraded(false);
                      setIsOpen(false);
                    }, 5000);
                  } catch (e) {
                    let message = "An error occurred";
                    try {
                      const serverMessage = e.response?.data?.error;
                      if (serverMessage) {
                        message = serverMessage;
                      }
                    } catch {}
                    dispatch(
                      addToast({
                        type: "error",
                        message: message,
                        timer: 5,
                      })
                    );
                  } finally {
                    setUpgrading(false);
                  }
                }}
              >
                {crmPrice !== "Coming Soon" ? (
                  "Enable Customer Engagement Now"
                ) : crmPrice === "Coming Soon" ? (
                  "Coming Soon"
                ) : (
                  <Loader size={8} hideText />
                )}
              </PrimaryButton>
            )}
          </div>
        </div>
      </ModalBasic>
    </ModalBasic>
  );
};

export default PlanModal;
